import http from '@/lib/http'
import type { ProductDetail, ProductRow } from '@/types/product'
import type { ProductCategory } from '@/types/order'
import { useQuery } from '@tanstack/react-query'

/**
 * Fetch all products (variant-level rows)
 */
export const fetchProducts = async (): Promise<ProductRow[]> => {
  const data = (await http.get('/products')) as unknown as ProductRow[]
  return data
}

/**
 * Fetch a single product by hash (with variations)
 */
export const fetchProductByVariationHash = async (hash: string): Promise<ProductDetail> => {
  const data = (await http.get(`/products/variant/${hash}`)) as unknown as ProductDetail
  return data
}

/**
 * Fetch a single product by product hash (with variations)
 */
export const fetchProductByHash = async (hash: string): Promise<ProductDetail> => {
  const data = (await http.get(`/products/${hash}`)) as unknown as ProductDetail
  return data
}

/**
 * Hook to fetch products list
 */
export function useProductsQuery() {
  const query = useQuery({
    queryKey: ['products'],
    queryFn: fetchProducts
  })

  return {
    products: query.data ?? [],
    count: query.data?.length ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error
  }
}

/**
 * Hook to fetch a single product
 */
export function useProductVariationQuery(hash: string | undefined) {
  return useQuery({
    queryKey: ['product-variation', hash],
    queryFn: () => fetchProductByVariationHash(hash!),
    enabled: !!hash
  })
}

/**
 * Hook to fetch a single product by product hash
 */
export function useProductByHashQuery(hash: string | undefined) {
  return useQuery({
    queryKey: ['product', hash],
    queryFn: () => fetchProductByHash(hash!),
    enabled: !!hash
  })
}

/**
 * Fetch product categories for a list of product hashes.
 * Returns a map from product hash to its categories.
 */
export const fetchProductCategories = async (productHashes: string[]): Promise<Record<string, ProductCategory[]>> => {
  const data = (await http.get('/products/categories', {
    params: { productHashes: JSON.stringify(productHashes) }
  })) as unknown as Record<string, ProductCategory[]>
  return data
}

/**
 * Hook to fetch product categories by product hashes.
 */
export function useProductCategoriesQuery(productHashes: string[]) {
  return useQuery({
    queryKey: ['product-categories', productHashes],
    queryFn: () => fetchProductCategories(productHashes),
    enabled: productHashes.length > 0
  })
}
